import React from "react";
import { FaFileInvoiceDollar } from "react-icons/fa";

/**
 * Invoice header block rendered at the top of the payment page's
 * main column — above the line-items table and the CheckoutPanel.
 *
 * Shows the invoice number, issue + due dates, a small icon, and
 * the From / To parties in two columns. The gradient sidebar
 * already surfaces the creator's @username + address + total;
 * this block is the "business document" metadata that makes the
 * page feel like an invoice rather than a receipt.
 *
 * Due-date line is omitted when the invoice has no due date
 * (dueDate: null on the /info response). Recipient block
 * gracefully degrades when the invoice is public (no @username +
 * no address + optional recipientNote only).
 */
export function InvoiceHeader({ invoice }) {
  if (!invoice) return null;

  const issueDateStr = formatDate(invoice.issueDate);
  const dueDateStr = invoice.dueDate ? formatDate(invoice.dueDate) : null;

  const creatorLabel = invoice.creator?.username
    ? `@${invoice.creator.username}`
    : invoice.creator?.displayAddress || "";
  const recipientPrimary = invoice.recipient?.username
    ? `@${invoice.recipient.username}`
    : invoice.recipient?.note || invoice.recipient?.displayAddress || null;

  return (
    <div className="pay-invoice-header">
      <div className="pay-invoice-header__top">
        <div className="pay-invoice-header__title">
          <FaFileInvoiceDollar size={18} />
          <span>Invoice {invoice.invoiceNumber}</span>
        </div>
        <div className="pay-invoice-header__dates">
          <div>
            <span className="pay-invoice-header__label">Issued</span>
            <span className="pay-invoice-header__value">{issueDateStr}</span>
          </div>
          {dueDateStr && (
            <div>
              <span className="pay-invoice-header__label">Due</span>
              <span className="pay-invoice-header__value">{dueDateStr}</span>
            </div>
          )}
        </div>
      </div>
      <div className="pay-invoice-header__parties">
        <div>
          <span className="pay-invoice-header__label">From</span>
          <div className="pay-invoice-header__value">{creatorLabel}</div>
          {invoice.creator?.displayAddress && invoice.creator.username && (
            <div className="pay-invoice-header__subvalue">
              {invoice.creator.displayAddress}
            </div>
          )}
        </div>
        {recipientPrimary && (
          <div>
            <span className="pay-invoice-header__label">Billed to</span>
            <div className="pay-invoice-header__value">{recipientPrimary}</div>
            {invoice.recipient?.note && invoice.recipient?.username && (
              <div className="pay-invoice-header__subvalue">
                {invoice.recipient.note}
              </div>
            )}
          </div>
        )}
      </div>
      {invoice.description && (
        <p className="pay-invoice-header__description">
          {invoice.description}
        </p>
      )}
    </div>
  );
}

/** Locale-pinned date formatter so "Apr 22, 2026" renders the same
 *  regardless of the payer's browser locale. Matches the pattern
 *  ReceiptCard uses for timestamps. */
function formatDate(ms) {
  if (!ms || !Number.isFinite(Number(ms))) return "";
  try {
    return new Date(Number(ms)).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return new Date(Number(ms)).toISOString().slice(0, 10);
  }
}
